import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import UnauthedFooter from 'src/components/UnauthedFooter/UnauthedFooter'
import { AuthTypes, SupportedBrands } from 'src/enums'
import Trns from 'src/localization/Trns'
import {
  deleteCognitoSignout,
  deleteCspEmulatedSessions,
  deleteSignout,
} from 'src/redux/actions/auth.actions'
import {
  selectAuthTypeFromState,
  selectIsCspAuthenticatedFromState,
} from 'src/redux/selectors/auth.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import { useWhitelabel } from 'src/utils/whitelabel.utils'
import styled, { css } from 'styled-components'

interface BrandAgnosticFooterProps {
  className?: string
  logOutOption?: boolean
  step?: number
  topMargin?: boolean
}

const DividerLineDiv = styled.div`
  border-left: 1px solid ${styleConstants.colors.gray_darkest};
  height: 15px;
  margin: 0px 5px;
`

const BrandAgnosticFooter: React.FC<BrandAgnosticFooterProps> = (props) => {
  const { topMargin = true } = props
  const { whiteLabelIncludes } = useWhitelabel()
  const isExtendBrand = whiteLabelIncludes(SupportedBrands.Extend)
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const authType = useSelector(selectAuthTypeFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const location = useSelector(selectLocationFromState)
  const { analytics } = useAnalyticsContext()

  const dispatch = useDispatch()

  const translatedScreen = (): string => {
    switch (props.step) {
      case 1:
        return '/onboarding/name'
      case 2:
        return '/onboarding/info'
      case 3:
        return '/onboarding/register'
      default:
        return location.pathname
    }
  }

  const dispatchDeleteSignout = (): void => {
    if (authType === AuthTypes.auth0) {
      dispatch(
        deleteSignout(
          false,
          analytics,
          false,
          calculatedBackgroundLocation.pathname,
        ),
      )
    } else {
      dispatch(deleteCognitoSignout(false, analytics))
    }
  }

  const dispatchDeleteCspEmulatedSessions = (): void => {
    dispatch(deleteCspEmulatedSessions(false, false, analytics))
  }

  const haveQuestions: React.ReactElement = (
    <Trns
      source={
        'Need help? Visit our ${resourceCenter} for support and contact options.'
      }
      expressions={{
        resourceCenter: (
          <ResourceCenterLink
            size={HoverableLinkSizes.Small}
            underline
            regular
          />
        ),
      }}
    />
  )

  return (
    <div>
      <div
        css={css`
          display: flex;
          width: 100%;
          justify-content: center;
        `}
        className={props.className}
      >
        {isExtendBrand && (
          <UnauthedFooter
            topMargin={topMargin}
            css={css`
              width: auto;
              margin-right: 20px;
            `}
          />
        )}
        <div
          css={css`
            ${topMargin && 'margin-top: 40px;'}
            ${getFontCss(
              12,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
            display: flex;
            align-items: center;
          `}
        >
          <span>{haveQuestions}</span>
          {props.logOutOption && (
            <>
              <DividerLineDiv />
              <div
                css={css`
                  ${getFontCss(
                    12,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                <Trns
                  source={'Need to come back later? ${logOut}'}
                  expressions={{
                    logOut: (
                      <HoverableLink
                        size={HoverableLinkSizes.Small}
                        onClick={(): void => {
                          analytics?.track('[onboarding] footer - log out', {
                            screen: translatedScreen(),
                          })

                          if (isCspAuthenticated) {
                            dispatchDeleteCspEmulatedSessions()
                          } else {
                            dispatchDeleteSignout()
                          }
                        }}
                        underline
                        regular
                        ariaLabelSource={'Log out'}
                      >
                        <Trns source={'Log out'} />
                      </HoverableLink>
                    ),
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
      {!isExtendBrand && (
        <UnauthedFooter
          css={css`
            width: auto;
            margin-top: 10px;
          `}
        />
      )}
    </div>
  )
}

export default BrandAgnosticFooter
